import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { GetEsimInventoriesParams } from '@apis/esim';
import { getEsimInventoryCount } from '@apis/esim';

import { dropEmptyValues } from '../../utils/common';

const COUNT_KEYS = [
  'stockId',
  'esimType',
  'esimStatus',
  'expireAtFrom',
  'expireAtTo',
  'parameterType',
  'parameterValue',
] as const;

type CountKey = (typeof COUNT_KEYS)[number];

/**
 * Inventory count query hook.
 *
 * - Uses the same filter normalization strategy as the list query hook.
 * - Builds a stable queryKey from selected filter fields to avoid cache collisions.
 * - Keeps the previous count while fetching a new count to prevent UI flicker.
 */
export const useInventoryCount = (searchParam: GetEsimInventoriesParams) => {
  /**
   * API request params:
   * Remove empty values from the filter object while keeping valid values.
   */
  const cleaned = useMemo(
    () => dropEmptyValues(searchParam, [...COUNT_KEYS]),
    [searchParam],
  );

  /**
   * Query key parts:
   * Use a stable list of primitive values rather than the filter object reference.
   */
  const keyParts = useMemo(
    () => COUNT_KEYS.map((k: CountKey) => (cleaned as Record<CountKey, unknown>)[k] ?? null),
    [cleaned],
  );

  return useQuery<number, Error>({
    queryKey: ['esimInventoriesCount', ...keyParts],
    queryFn: () => getEsimInventoryCount(cleaned),

    // React Query v5: keep previous data while fetching to avoid count "jumping" in UI.
    placeholderData: (prev) => prev,
  });
};
